import * as React from 'react';

import {
  enumPurchaseLoadStatus,
  PurchaseLoadStatus,
} from '@rematter/frontend-lib-api-client';
import { ConfirmPopoverButton, Row, TouchButton } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheckCircle, LucidePause, LucidePlay } from 'lucide-react';

import {
  $completeInventoryScaleTicket,
  $completePriceScaleTicket,
  $revertScaleTicket,
  $scaleTicketDetail,
} from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { PurchaseLoadPaymentRecord } from '../../../types/purchaseLoad';
import { getForwardSteps } from '../hooks/useStepperTransition';
import { FinishScaleTicketModal } from '../shared/FinishScaleTicketModal';
import { VoidTicketButton } from '../shared/VoidTicketButton';

import { ScaleTicketStatusStepper } from './ScaleTicketStatusStepper';

type HeaderActionsProps = {
  status: PurchaseLoadStatus;
  isProcessing: boolean;
  ticketId: string | null | undefined;
  onHold: () => void;
  onVoid: (voidReason?: string) => void;
  onRelease: () => void;
  onPay: () => void;
  grandTotal?: string;
  facilityId?: string;
  purchaseLoadId?: string;
  existingPayments?: PurchaseLoadPaymentRecord[];
  ticketReceivedDate?: string;
  showStepper?: boolean;
};

type FinishButtonProps = {
  status: PurchaseLoadStatus;
  isProcessing: boolean;
  facilityId?: string;
  onPress: () => void;
};

type HoldButtonProps = {
  status: PurchaseLoadStatus;
  onHold: () => void;
  isProcessing: boolean;
};

type ReleaseButtonProps = {
  status: PurchaseLoadStatus;
  onRelease: () => void;
  isProcessing: boolean;
};

const VOID_VISIBLE_STATUSES: PurchaseLoadStatus[] = [
  enumPurchaseLoadStatus.Open,
  enumPurchaseLoadStatus.InventoryComplete,
  enumPurchaseLoadStatus.PriceComplete,
  enumPurchaseLoadStatus.Held,
];

//
// ⚛️ ⚛️ ⚛️ FINISH BUTTON ⚛️ ⚛️ ⚛️
//
const FINISH_VISIBLE_STATUSES: PurchaseLoadStatus[] = [
  enumPurchaseLoadStatus.Open,
  enumPurchaseLoadStatus.InventoryComplete,
  enumPurchaseLoadStatus.PriceComplete,
  enumPurchaseLoadStatus.Held,
];

const FinishButton = ({
  status,
  isProcessing,
  facilityId,
  onPress,
}: FinishButtonProps) => {
  const t = useTranslations();

  if (!FINISH_VISIBLE_STATUSES.includes(status)) return null;

  return (
    <TouchButton
      variant="outline"
      color="success"
      size="xl"
      onPress={onPress}
      isDisabled={isProcessing || !facilityId}
      className="@FinishButton"
    >
      <Row gap1>
        <LucideCheckCircle size={16} />
        {t.scaleTicketPro.stepper.finishButton}
      </Row>
    </TouchButton>
  );
};

//
// ⚛️ ⚛️ ⚛️ HOLD BUTTON ⚛️ ⚛️ ⚛️
//

const HOLD_VISIBLE_STATUSES: PurchaseLoadStatus[] = [
  enumPurchaseLoadStatus.Open,
];
const HoldButton = ({ status, onHold, isProcessing }: HoldButtonProps) => {
  const t = useTranslations();

  if (!HOLD_VISIBLE_STATUSES.includes(status)) return null;

  return (
    <ConfirmPopoverButton
      onConfirm={onHold}
      message={t.scaleTicketPro.holdTicket.description}
      confirmLabel={t.scaleTicketPro.holdTicket.confirmButton}
      cancelLabel={t.common.cancel}
      color="primary"
      className="@HoldButton"
      contentClassName=" max-w-80"
      renderTrigger={() => (
        <TouchButton
          variant="outline"
          isDisabled={isProcessing}
          size="xl"
          className=""
          onPress={() => null}
        >
          <Row gap1>
            <LucidePause size={16} />
            {t.scaleTicketPro.detail.actions.hold}
          </Row>
        </TouchButton>
      )}
      isLoading={isProcessing}
    />
  );
};

//
// ⚛️ ⚛️ ⚛️ RELEASE BUTTON ⚛️ ⚛️ ⚛️
//
const ReleaseButton = ({
  status,
  onRelease,
  isProcessing,
}: ReleaseButtonProps) => {
  const t = useTranslations();

  if (status !== enumPurchaseLoadStatus.Held) return null;

  return (
    <TouchButton
      variant="outline"
      color="warning"
      size="xl"
      onPress={onRelease}
      isDisabled={isProcessing}
      className="@ReleaseButton"
    >
      <Row gap1>
        <LucidePlay size={16} />
        {t.scaleTicketPro.detail.actions.release}
      </Row>
    </TouchButton>
  );
};

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET HEADER ACTIONS ⚛️ ⚛️ ⚛️
//
export const InboundTicketHeaderActions = ({
  status,
  isProcessing,
  ticketId,
  onHold,
  onVoid,
  onRelease,
  onPay,
  grandTotal,
  facilityId,
  purchaseLoadId,
  existingPayments,
  ticketReceivedDate,
  showStepper = true,
}: HeaderActionsProps) => {
  const [isFinishing, setIsFinishing] = React.useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = React.useState(false);
  const [statusBeforeFinish, setStatusBeforeFinish] =
    React.useState<PurchaseLoadStatus | null>(null);

  const { refetch } = useAtomValue($scaleTicketDetail);
  const completeInventory = useSetAtom($completeInventoryScaleTicket);
  const completePrice = useSetAtom($completePriceScaleTicket);
  const revertStatus = useSetAtom($revertScaleTicket);

  const toast = useToast();

  const combinedProcessing = isProcessing || isFinishing;

  const handleFinish = React.useCallback(async () => {
    if (!ticketId) return;
    setIsFinishing(true);
    try {
      setStatusBeforeFinish(status);
      const steps = getForwardSteps(status, 'PriceComplete');
      for (const step of steps) {
        if (step === 'InventoryComplete') {
          await completeInventory({ id: ticketId });
        } else if (step === 'PriceComplete') {
          await completePrice({ id: ticketId });
        }
      }
      setIsFinishModalOpen(true);
    } catch (err) {
      setStatusBeforeFinish(null);
      if (err instanceof Error) {
        toast.error(err.message);
      }
      await refetch();
    } finally {
      setIsFinishing(false);
    }
  }, [ticketId, status, completeInventory, completePrice, toast, refetch]);

  const handleFinishModalClose = React.useCallback(async () => {
    setIsFinishModalOpen(false);
    if (statusBeforeFinish && ticketId) {
      try {
        await revertStatus({ id: ticketId, targetStatus: statusBeforeFinish });
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err.message);
        }
        await refetch();
      }
    }
    setStatusBeforeFinish(null);
  }, [statusBeforeFinish, ticketId, revertStatus, toast, refetch]);

  return (
    <>
      {VOID_VISIBLE_STATUSES.includes(status) && (
        <VoidTicketButton
          isDisabled={combinedProcessing}
          isProcessing={combinedProcessing}
          onVoid={onVoid}
          className="@VoidButton"
        />
      )}
      <HoldButton
        status={status}
        onHold={onHold}
        isProcessing={combinedProcessing}
      />
      <ReleaseButton
        status={status}
        onRelease={onRelease}
        isProcessing={combinedProcessing}
      />
      <FinishButton
        status={status}
        isProcessing={combinedProcessing}
        facilityId={facilityId}
        onPress={
          status === enumPurchaseLoadStatus.PriceComplete
            ? () => setIsFinishModalOpen(true)
            : handleFinish
        }
      />
      {showStepper && (
        <ScaleTicketStatusStepper
          ticketId={ticketId}
          status={status}
          isProcessing={combinedProcessing}
          facilityId={facilityId}
        />
      )}
      {purchaseLoadId && facilityId && grandTotal != null && (
        <FinishScaleTicketModal
          isOpen={isFinishModalOpen}
          onClose={handleFinishModalClose}
          onConfirm={async () => {
            setStatusBeforeFinish(null);
            setIsFinishModalOpen(false);
            await onPay();
          }}
          grandTotal={grandTotal}
          isProcessing={combinedProcessing}
          facilityId={facilityId}
          purchaseLoadId={purchaseLoadId}
          existingPayments={existingPayments}
          ticketReceivedDate={ticketReceivedDate}
        />
      )}
    </>
  );
};
